import { useState, useEffect } from 'react'
import {
    Box,
    Card,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { useEvents } from '../../hooks/useEvents'
import { PropertyEvent } from '../../types/propertyEvent'
import { AppRoutes, resolveRoute } from '../../helpers/Routes'
import { RootState } from '../../store'

const formatEventDateTime = (event: PropertyEvent, tz: string): string => {
    const start = new Date(event.starts_at)
    const end = new Date(event.ends_at)

    const startDateStr = start.toLocaleDateString('en-US', { timeZone: tz })
    const endDateStr = end.toLocaleDateString('en-US', { timeZone: tz })
    const sameDay = startDateStr === endDateStr

    if (event.is_all_day) {
        if (sameDay) {
            return start.toLocaleDateString('en-US', {
                timeZone: tz,
                weekday: 'short',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            })
        }
        return `${start.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric', year: 'numeric' })}`
    }

    const timeOptions: Intl.DateTimeFormatOptions = {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }

    if (sameDay) {
        const dateStr = start.toLocaleDateString('en-US', {
            timeZone: tz,
            weekday: 'short',
            month: 'long',
            day: 'numeric',
        })
        const startTime = start.toLocaleTimeString('en-US', timeOptions)
        const endTime = end.toLocaleTimeString('en-US', timeOptions)
        return `${dateStr} at ${startTime} - ${endTime}`
    }

    const startStr = `${start.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric' })} at ${start.toLocaleTimeString('en-US', timeOptions)}`
    const endStr = `${end.toLocaleDateString('en-US', { timeZone: tz, month: 'long', day: 'numeric', year: 'numeric' })} at ${end.toLocaleTimeString('en-US', timeOptions)}`
    return `${startStr} - ${endStr}`
}

const getMonthAbbr = (event: PropertyEvent, tz: string) => {
    return new Date(event.starts_at).toLocaleDateString('en-US', { timeZone: tz, month: 'short' }).toUpperCase()
}

const getDay = (event: PropertyEvent, tz: string) => {
    return new Date(event.starts_at).toLocaleDateString('en-US', { timeZone: tz, day: 'numeric' })
}

interface EventCardProps {
    event: PropertyEvent
    timezone: string
    onClick: () => void
}

const EventCard: React.FC<EventCardProps> = ({ event, timezone, onClick }) => {
    const theme = useTheme()

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                gap: 2.5,
                background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 2.5,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                cursor: 'pointer',
                transition: 'border-color 0.2s ease, transform 0.15s ease',
                '&:hover': {
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-1px)',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 52,
                    height: 56,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.primary.main}11 100%)`,
                    border: `1px solid ${theme.palette.primary.main}33`,
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        lineHeight: 1,
                        letterSpacing: '0.05em',
                    }}
                >
                    {getMonthAbbr(event, timezone)}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        color: '#fff',
                        fontWeight: 700,
                        lineHeight: 1.2,
                    }}
                >
                    {getDay(event, timezone)}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <FlexDiv
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            color: '#fff',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {event.title}
                    </Typography>
                    {event.attachments.length > 0 && (
                        <FlexDiv
                            style={{
                                alignItems: 'center',
                                marginLeft: theme.spacing(1.5),
                                flexShrink: 0,
                            }}
                        >
                            <AttachFileIcon
                                sx={{
                                    color: '#8b949e',
                                    fontSize: 14,
                                    mr: 0.25,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: '#8b949e' }}
                            >
                                {event.attachments.length}
                            </Typography>
                        </FlexDiv>
                    )}
                </FlexDiv>

                <FlexDiv
                    style={{
                        alignItems: 'center',
                        marginTop: theme.spacing(0.5),
                    }}
                >
                    <AccessTimeIcon
                        sx={{ color: '#8b949e', fontSize: 14, mr: 0.5 }}
                    />
                    <Typography variant="body2" sx={{ color: '#8b949e' }}>
                        {formatEventDateTime(event, timezone)}
                    </Typography>
                </FlexDiv>

                <FlexDiv
                    style={{
                        alignItems: 'center',
                        marginTop: theme.spacing(0.5),
                        gap: theme.spacing(2),
                    }}
                >
                    {event.location && (
                        <FlexDiv style={{ alignItems: 'center' }}>
                            <LocationOnIcon
                                sx={{
                                    color: '#8b949e',
                                    fontSize: 14,
                                    mr: 0.5,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.55)' }}
                            >
                                {event.location}
                            </Typography>
                        </FlexDiv>
                    )}
                </FlexDiv>
            </Box>
        </Box>
    )
}

export const Events = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const { isLoading, eventList, getEvents } = useEvents()
    const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming')

    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const timezone =
        selectedLease?.apartment_primary_address?.timezone_id || 'UTC'

    useEffect(() => {
        getEvents({ time: activeTab }).catch(() => {})
    }, [activeTab])

    const handleTabChange = (
        _event: React.SyntheticEvent,
        value: 'upcoming' | 'past',
    ) => {
        setActiveTab(value)
    }

    const handleEventClick = (event: PropertyEvent) => {
        navigate(resolveRoute(AppRoutes.eventDetail, ':id', event.id))
    }

    return (
        <ResponsivePageContainer
            title="Events"
            subtitle="Community events at your property"
        >
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                    mb: 3,
                    '& .MuiTab-root': {
                        color: 'rgba(255,255,255,0.7)',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&.Mui-selected': {
                            color: '#fff',
                            fontWeight: 600,
                        },
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.primary.main,
                        height: 3,
                        borderRadius: 1.5,
                    },
                }}
            >
                <Tab value="upcoming" label="Upcoming" />
                <Tab value="past" label="Past" />
            </Tabs>

            <Box
                sx={{
                    maxWidth: '700px',
                    width: '100%',
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            py: 10,
                        }}
                    >
                        <CircularProgress
                            size={36}
                            sx={{ color: theme.palette.primary.main }}
                        />
                    </Box>
                ) : eventList.length === 0 ? (
                    <Card
                        sx={{
                            background:
                                'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                            border: `1px solid ${theme.palette.grey[700]}`,
                            p: 5,
                            textAlign: 'center',
                        }}
                    >
                        <EventBusyIcon
                            sx={{
                                fontSize: 48,
                                color: 'rgba(255,255,255,0.2)',
                                mb: 2,
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                mb: 0.5,
                                fontWeight: 500,
                            }}
                        >
                            {activeTab === 'upcoming'
                                ? 'No upcoming events'
                                : 'No past events'}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.45)' }}
                        >
                            {activeTab === 'upcoming'
                                ? 'Check back later for new community events at your property.'
                                : 'No past events to show.'}
                        </Typography>
                    </Card>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {eventList.map((event) => (
                            <EventCard
                                key={event.id}
                                event={event}
                                timezone={timezone}
                                onClick={() => handleEventClick(event)}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </ResponsivePageContainer>
    )
}
